/**
 * Web Session SSO 引导：解决 App WebView 自动登录问题
 *
 * 工作机制：
 * - App 注入 web_session cookie 后打开页面（任意路由）。
 * - 本组件在挂载（且仅一次）时尝试 verify-web-session。
 * - 仅当：1) 当前未登录；或 2) localStorage 中残留旧的 auth-storage 但
 *   cookie 中存在 web_session（说明 App 想用新会话覆盖）时，才走 SSO。
 * - SSO 成功后写入 zustand → localStorage；失败则清理 stale auth-storage，
 *   让用户在 LoginPage 看到一致的"未登录"状态，避免出现"二次登录"问题。
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth';

const WEB_SESSION_COOKIE = 'web_session';

function readCookie(name: string): string | null {
  const cookies = typeof document !== 'undefined' ? document.cookie : '';
  if (!cookies) return null;
  const parts = cookies.split(';');
  for (const part of parts) {
    const [k, ...rest] = part.split('=');
    if (k && k.trim() === name) {
      return decodeURIComponent(rest.join('=').trim());
    }
  }
  return null;
}

function isJwtExpired(token: string | null): boolean {
  if (!token) return true;
  const segs = token.split('.');
  if (segs.length !== 3) return true;
  try {
    const payload = JSON.parse(atob(segs[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp !== 'number') return false;
    return Date.now() / 1000 > payload.exp;
  } catch {
    return true;
  }
}

export function WebSessionBootstrap() {
  const ran = useRef(false);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const sessionToken = readCookie(WEB_SESSION_COOKIE);
    const { isAuthenticated, accessToken } = useAuthStore.getState();
    const tokenExpired = isJwtExpired(accessToken);

    // 如果 localStorage 中的 JWT 已过期，先清理，避免 stale 登录状态干扰
    if (isAuthenticated && tokenExpired) {
      console.log('🧹 检测到本地 JWT 已过期，清理登录态');
      clearAuth();
    }

    // 没有 web_session cookie：完全依赖 localStorage 的 JWT（如果有）
    if (!sessionToken) {
      console.log('ℹ️  未发现 web_session cookie，跳过 SSO');
      return;
    }

    // 已经持有有效 JWT 且不需要重新走 SSO：跳过
    if (isAuthenticated && !tokenExpired) {
      console.log('✅ 本地已有有效 JWT，跳过 SSO');
      return;
    }

    console.log('🔍 发现 web_session cookie，开始验证...');
    (async () => {
      try {
        const response = await fetch('/api/auth/verify-web-session', {
          method: 'GET',
          credentials: 'include',
        });
        const text = await response.text();
        let data: any = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          data = null;
        }

        if (!response.ok || !data || !data.success || !data.data?.user || !data.data?.accessToken) {
          console.warn('❌ web_session 验证失败:', response.status, data?.message);
          // 清理可能存在的 stale 登录态，避免页面以"假登录"状态运行
          clearAuth();
          // 同时清掉 vipPermissions / quotaInfo
          try {
            localStorage.removeItem('vipPermissions');
            localStorage.removeItem('quotaInfo');
          } catch {
            /* ignore */
          }
          return;
        }

        setAuth(data.data.user, data.data.accessToken, data.data.refreshToken || '');
        if (data.data.permissionInfo) {
          try {
            localStorage.setItem('vipPermissions', JSON.stringify(data.data.permissionInfo));
          } catch {
            /* ignore */
          }
        }
        if (data.data.quotaInfo) {
          try {
            localStorage.setItem('quotaInfo', JSON.stringify(data.data.quotaInfo));
          } catch {
            /* ignore */
          }
        }
        console.log('✅ web_session SSO 成功:', data.data.user?.username);
      } catch (e) {
        console.error('❌ web_session 验证异常:', e);
      }
    })();
  }, [setAuth, clearAuth]);

  return null;
}
